import React, { VFC } from 'react';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import { DataServiceHookResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import {
  useWizardGoToNextStep,
  useWizardGoToPreviousStep,
  useWizardStepData,
} from '../../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { useAuthorizationPayrollLoadingState } from '../../../contexts/Payroll/hooks';
import { InitiateMandateResponseBody } from '../../../domain/Authorization/entities/InitiateMandateResponseDTO';
import { ErrorContent } from '../../../../../content-composer/src/components/Partials/ErrorContent';
import { ECOMMBXLoader } from '../../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { InAPP2FAStep } from '../../../../../content-composer/src/components/ClientSideWizard/Steps';
import { AuthenticationMethodEnum } from '../../../../../content-composer/src/components/ClientSideWizard/Steps/InApp2FAStep/InAPP2FAStep';
import { usePayrollService } from './usePayrollService';

export interface PayrollInitiateStepProps {
  authAction: string | null;
  hasRejectionReason?: boolean;
  onToggleDrawer?: () => void;
  resetFlow?: () => void;
  hasPayrollInfo?: boolean;
}

const PayrollInitiateStep: VFC<PayrollInitiateStepProps> = ({
  authAction,
  hasRejectionReason = false,
  onToggleDrawer,
  resetFlow,
  hasPayrollInfo = false,
}) => {
  const { t } = useTranslation();
  const goToNextWizardStep = useWizardGoToNextStep();
  const goToPreviousStep = useWizardGoToPreviousStep();
  const previousStepData = useWizardStepData(
    hasRejectionReason ? 'rejectReasonStep' : hasPayrollInfo ? 'payrollInfo' : undefined,
  ) as {
    values: { cancellationReason?: string };
  };

  // loading state
  const isLoadingCheckAuthorization = useAuthorizationPayrollLoadingState('isLoadingCheckAuthorization');

  // service
  const {
    data: initiateMandateData,
    isLoading: isLoadingInitiateAuthorization,
    error: initiateAuthorizationError,
    mutate: refetchInitiateMandateData,
  } = (
    usePayrollService('initiate') as (
      authAction: Nullable<string>,
      cancellationReason?: string,
    ) => DataServiceHookResponse<InitiateMandateResponseBody>
  )(authAction, previousStepData.values?.cancellationReason);

  const isLoading = isLoadingCheckAuthorization || isLoadingInitiateAuthorization;
  const error = initiateAuthorizationError;

  return (
    <>
      {error && !isLoading && (
        <ErrorContent
          code={error?.code || '404'}
          onClickBtn={refetchInitiateMandateData}
          subtitle={error?.message || t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Execute.Subtitle')}
          btnLabel={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.BtnLabel')}
          message={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Message')}
        />
      )}
      {isLoading && <ECOMMBXLoader size='small' />}
      {!error && isEmpty(error) && !isLoading && (
        <InAPP2FAStep
          title={t(
            authAction === 'approve'
              ? 'authorizationsListPage.ApproveRejectFlow.MandateInitiateStep.inApp2FA.Approve.Title'
              : 'authorizationsListPage.ApproveRejectFlow.MandateInitiateStep.inApp2FA.Reject.Title',
          )}
          hasOverflowYAuto={true}
          resetFlow={resetFlow}
          onToggleDrawer={onToggleDrawer}
          authenticationMethod={initiateMandateData?.authenticationMethod as AuthenticationMethodEnum}
          externalAuthorizationId={initiateMandateData?.authorizationId}
          externalOnGoToNextWizardStep={(data: any) =>
            goToNextWizardStep({ ...data, requestId: initiateMandateData?.requestId })
          }
          externalOnCancel={() => (onToggleDrawer ? onToggleDrawer() : goToPreviousStep())}
        />
      )}
    </>
  );
};

export { PayrollInitiateStep };
